import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { dict } from "@/lib/i18n";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function AppProvider({ children }) {
  const [lang, setLang] = useState(load("ce_lang", "id"));
  const [currency, setCurrency] = useState(load("ce_currency", "IDR"));
  const [config, setConfig] = useState({ usd_rate: 16000 });
  const [site, setSite] = useState(null);
  const [user, setUser] = useState(undefined);
  const [items, setItems] = useState(load("ce_cart", []));

  useEffect(() => { localStorage.setItem("ce_lang", JSON.stringify(lang)); }, [lang]);
  useEffect(() => { localStorage.setItem("ce_currency", JSON.stringify(currency)); }, [currency]);
  useEffect(() => { localStorage.setItem("ce_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => {
    if (!user) return undefined;
    const id = setTimeout(() => { api.put("/me/cart", { items }).catch(() => {}); }, 1500);
    return () => clearTimeout(id);
  }, [items, user]);
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  const loadSite = useCallback(() => api.get("/site").then((r) => setSite(r.data)).catch(() => {}), []);
  useEffect(() => { loadSite(); }, [loadSite]);
  useEffect(() => {
    const el = document.getElementById("ce-favicon");
    if (el && site?.favicon_url) el.href = fileUrl(site.favicon_url);
    const font = site?.font_family;
    if (!font) { return; }
    let link = document.getElementById("ce-font-link");
    if (!link) { link = document.createElement("link"); link.id = "ce-font-link"; link.rel = "stylesheet"; document.head.appendChild(link); }
    link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, "+")}:wght@400;700&display=swap`;
    document.documentElement.style.setProperty("--ce-font", `"${font}", Georgia, serif`);
  }, [site]);
  useEffect(() => {
    const block = (e) => { if (e.target?.tagName === "IMG") { e.preventDefault(); } };
    document.addEventListener("contextmenu", block);
    document.addEventListener("dragstart", block);
    return () => { document.removeEventListener("contextmenu", block); document.removeEventListener("dragstart", block); };
  }, []);

  const refreshUser = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); } catch { setUser(null); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setUser(null); return; }
    refreshUser();
  }, [refreshUser]);

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(null); };
  const t = useCallback((k) => dict[lang][k] || dict.en[k] || k, [lang]);
  const fmt = useCallback((idr) => {
    if (currency === "USD") return "$" + (idr / (config.usd_rate || 16000)).toFixed(2);
    return "Rp " + Math.round(idr).toLocaleString("id-ID");
  }, [currency, config.usd_rate]);
  const pick = useCallback((obj, field) => (lang === "id" && obj?.[`${field}_id`]) || obj?.[field], [lang]);

  const cart = useMemo(() => ({
    items,
    count: items.reduce((s, i) => s + i.qty, 0),
    subtotal: items.reduce((s, i) => s + i.price * i.qty, 0),
    add: (item) => setItems((prev) => {
      const key = [item.product_id, JSON.stringify(item.variant || {}), item.session_id || ""].join("|");
      const single = item.type === "digital" || item.type === "course";
      const found = prev.find((p) => p.key === key);
      if (found) return prev.map((p) => (p.key === key ? { ...p, qty: single ? 1 : p.qty + item.qty } : p));
      return [...prev, { ...item, key, qty: single ? 1 : item.qty }];
    }),
    setQty: (key, qty) => setItems((prev) => prev.map((p) => (p.key === key ? { ...p, qty: Math.max(1, qty) } : p))),
    remove: (key) => setItems((prev) => prev.filter((p) => p.key !== key)),
    clear: () => setItems([]),
  }), [items]);

  const value = { lang, setLang, currency, setCurrency, config, site, loadSite, user, setUser, refreshUser, logout, t, fmt, pick, cart };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
